import { request } from 'utils/request';
import { header } from 'utils/auth';
import type { NurturingPlanVersion } from 'app/pages/NurturingPlanGeneratorPage/types';
import type { NurturingPlanSettingsBody } from 'app/pages/NurturingPlanGeneratorPage/utils/planSettingsMapping';

const SETTINGS_TYPE = 'NURTURING_PLAN_GENERATOR';

const jsonHeaders = () =>
  new Headers({
    'Content-Type': 'application/json',
    Authorization: header(),
  });

const assertOk = (res: any, fallbackMsg: string) => {
  const status = res?.statusCode ?? res?.myAutoIQ_status_code ?? res?.errorCode;
  const msg = res?.message ?? res?.myAutoIQ_error_message;
  if (typeof status === 'number' && status >= 400) {
    throw new Error(msg || fallbackMsg);
  }
  return res;
};

/**
 * Fetch the saved nurturing plan config for a dealer (optionally a specific
 * version). Mirrors the journey settings GET but with the plan settings type.
 *
 * GET /api/v2/dealer-settings?dealer=<id>&type=NURTURING_PLAN_GENERATOR&version=<v>
 */
export const fetchNurturingPlanSettings = async ({
  dealershipId,
  version,
}: {
  dealershipId: string;
  version?: number | null;
}): Promise<any> => {
  try {
    const versionParam = version != null ? `&version=${version}` : '';
    const requestURL = `/api/v2/dealer-settings?dealer=${dealershipId}&type=${SETTINGS_TYPE}${versionParam}`;
    const response: any = await request(requestURL, {
      method: 'GET',
      headers: jsonHeaders(),
    });
    if (!response) {
      throw new Error('Failed to fetch nurturing plan settings from API');
    }
    return await response;
  } catch (error) {
    console.error('Error fetching nurturing plan settings:', error);
    throw error;
  }
};

/**
 * Persist the nurturing plan config for a dealer (optionally a specific
 * version).
 *
 * PUT /api/v2/dealer-settings?dealer_id=<id>&type=NURTURING_PLAN_GENERATOR&version=<v>
 */
export const saveNurturingPlanSettings = async ({
  dealershipId,
  payload,
  version,
}: {
  dealershipId: string;
  payload: { dealer_id: string } & NurturingPlanSettingsBody;
  version?: number | null;
}): Promise<any> => {
  try {
    const versionParam = version != null ? `&version=${version}` : '';
    const requestURL = `/api/v2/dealer-settings?dealer_id=${dealershipId}&type=${SETTINGS_TYPE}${versionParam}`;
    const response: any = await request(requestURL, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response) {
      throw new Error('Failed to save nurturing plan settings');
    }
    return assertOk(await response, 'Failed to save nurturing plan settings');
  } catch (error) {
    console.error('Error saving nurturing plan settings:', error);
    throw error;
  }
};

/**
 * Primary segment names for a dealer (DEALER_SEGMENTS_CONFIG).
 *
 * GET /api/v2/dealer-settings/nurturing-plan/available-segments?dealer_id=
 */
export const fetchAvailablePlanSegments = async ({
  dealerId,
}: {
  dealerId: string;
}): Promise<string[]> => {
  try {
    const requestURL = `/api/v2/dealer-settings/nurturing-plan/available-segments?dealer_id=${dealerId}`;
    const response: any = await request(requestURL, {
      method: 'GET',
      headers: jsonHeaders(),
    });
    if (!response) {
      throw new Error('Failed to fetch dealer segments');
    }
    const res = assertOk(await response, 'Failed to fetch dealer segments');
    const body = res?.body ?? res?.data ?? res;
    const segments = body?.segments;
    if (!Array.isArray(segments)) {
      throw new Error('Dealer segment settings are not configured.');
    }
    return segments.filter((s: unknown) => typeof s === 'string' && s.trim()).map((s: string) => s.trim());
  } catch (error) {
    console.error('Error fetching available plan segments:', error);
    throw error;
  }
};

export interface NurturingPlanInitializeReadiness {
  ready: boolean;
  reason?: string;
}

const extractResponseBody = (res: any) => res?.body ?? res?.data ?? res;

/**
 * Whether CDP/lead analysis has enough data for CDP-based initialize.
 *
 * GET /api/v2/dealer-settings/nurturing-plan/initialize-readiness?dealer_id=
 */
export const fetchNurturingPlanInitializeReadiness = async ({
  dealerId,
}: {
  dealerId: string;
}): Promise<NurturingPlanInitializeReadiness> => {
  try {
    const requestURL = `/api/v2/dealer-settings/nurturing-plan/initialize-readiness?dealer_id=${dealerId}`;
    const response: any = await request(requestURL, {
      method: 'GET',
      headers: jsonHeaders(),
    });
    if (!response) {
      throw new Error('Failed to fetch nurturing plan initialize readiness');
    }
    const res = assertOk(await response, 'Failed to fetch nurturing plan initialize readiness');
    const body = extractResponseBody(res);
    return {
      ready: Boolean(body?.ready),
      reason: typeof body?.reason === 'string' ? body.reason : undefined,
    };
  } catch (error) {
    console.error('Error fetching nurturing plan initialize readiness:', error);
    throw error;
  }
};

/**
 * Initialize the nurturing plan config for a dealer (creates CREATED v1).
 * Uses lead analysis / CDP data only; throws when analysis is incomplete.
 *
 * POST /api/v2/dealer-settings/nurturing-plan/initialize
 */
export const initializeNurturingPlanSettings = async ({
  dealerId,
}: {
  dealerId: string;
}): Promise<any> => {
  try {
    const requestURL = `/api/v2/dealer-settings/nurturing-plan/initialize`;
    const response: any = await request(requestURL, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ dealer_id: dealerId }),
    });
    if (!response) {
      throw new Error('Failed to initialize nurturing plan settings');
    }
    return assertOk(await response, 'Failed to initialize nurturing plan settings');
  } catch (error) {
    console.error('Error initializing nurturing plan settings:', error);
    throw error;
  }
};

/**
 * Initialize with DEFAULT_PLAN_NUMERIC and dealer primary segments (even split).
 *
 * POST /api/v2/dealer-settings/nurturing-plan/initialize-default
 */
export const initializeNurturingPlanSettingsDefault = async ({
  dealerId,
}: {
  dealerId: string;
}): Promise<any> => {
  try {
    const requestURL = `/api/v2/dealer-settings/nurturing-plan/initialize-default`;
    const response: any = await request(requestURL, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ dealer_id: dealerId }),
    });
    if (!response) {
      throw new Error('Failed to initialize nurturing plan with default settings');
    }
    return assertOk(
      await response,
      'Failed to initialize nurturing plan with default settings',
    );
  } catch (error) {
    console.error('Error initializing nurturing plan with default settings:', error);
    throw error;
  }
};

/**
 * Publish a nurturing plan version, making it the active config.
 *
 * POST /api/v2/dealer-settings/nurturing-plan/publish
 */
export const publishNurturingPlan = async ({
  dealerId,
  settingsId,
}: {
  dealerId: string;
  settingsId: string;
}): Promise<any> => {
  try {
    const requestURL = `/api/v2/dealer-settings/nurturing-plan/publish`;
    const response: any = await request(requestURL, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ dealer_id: dealerId, settings_id: settingsId }),
    });
    if (!response) {
      throw new Error('Failed to publish nurturing plan');
    }
    return assertOk(await response, 'Failed to publish nurturing plan');
  } catch (error) {
    console.error('Error publishing nurturing plan:', error);
    throw error;
  }
};

/**
 * Fetch the list of saved nurturing plan versions for a dealer.
 *
 * GET /api/v2/dealer-settings/nurturing-plan/versions?dealer_id=<id>
 */
export const fetchNurturingPlanVersions = async ({
  dealerId,
}: {
  dealerId: string;
}): Promise<NurturingPlanVersion[]> => {
  try {
    const requestURL = `/api/v2/dealer-settings/nurturing-plan/versions?dealer_id=${dealerId}`;
    const response: any = await request(requestURL, {
      method: 'GET',
      headers: jsonHeaders(),
    });
    if (!response) {
      throw new Error('Failed to fetch nurturing plan versions');
    }
    const res = await response;
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.body)) return res.body;
    return [];
  } catch (error) {
    console.error('Error fetching nurturing plan versions:', error);
    throw error;
  }
};

/**
 * Launches the dealership (starts Continuous Nurturing Automation).
 *
 * POST /api/v2/dealers/<dealerId>/launch
 */
export const launchDealerForNurturingPlan = async ({
  dealerId,
}: {
  dealerId: string;
}): Promise<any> => {
  try {
    const requestURL = `/api/v2/dealers/${dealerId}/launch`;
    const response: any = await request(requestURL, {
      method: 'POST',
      headers: jsonHeaders(),
    });

    if (!response) {
      throw new Error('Failed to launch dealer');
    }

    return assertOk(await response, 'Failed to launch dealer');
  } catch (error) {
    console.error('Error launching dealer for nurturing plan:', error);
    throw error;
  }
};
